import { useEffect } from 'react';
import { create } from 'zustand';
import { readJson, removeStorage, writeJson } from '@/lib/storage';
import { CONSENT_STORAGE_KEY, CONSENT_VERSION } from './constants';

// Defined in `./constants` (dependency-free: the build's Google Tag Manager snippet reads them too).
export { CONSENT_STORAGE_KEY, CONSENT_VERSION };

export type ConsentDecision = 'unset' | 'accepted' | 'rejected';

interface StoredConsent {
  v: number;
  decision: Exclude<ConsentDecision, 'unset'>;
  decidedAt: string;
  /** `false` while the acceptance has not reached the database yet (the server was out of reach); a later visit retries. */
  saved?: boolean;
}

function parseStored(value: unknown): StoredConsent | null {
  if (typeof value !== 'object' || value === null) return null;
  const { v, decision, decidedAt, saved } = value as Partial<StoredConsent>;
  if (v !== CONSENT_VERSION) return null; // the disclaimer's wording changed → ask again
  if (decision !== 'accepted' && decision !== 'rejected') return null;
  return {
    v,
    decision,
    decidedAt: typeof decidedAt === 'string' ? decidedAt : '',
    ...(saved === false ? { saved } : {}),
  };
}

interface ConsentState {
  /** `unset` until the visitor chooses. Nothing analytics-related may load while it is not `accepted`. */
  decision: ConsentDecision;
  decidedAt: string | null;
  /** True once the stored choice has been read on the client (the bar never renders before this). */
  hydrated: boolean;
  accept: () => void;
  reject: () => void;
  /** Forget the choice so the bar asks again ("Cookie preferences" in the footer). */
  reset: () => void;
  hydrate: () => void;
}

/**
 * Save the acceptance in the database (a lazy chunk). On success the stored choice is marked saved; if the server cannot be
 * reached it stays `saved: false` and the next visit tries again — the visitor is never held up by it.
 */
function saveToServer(decision: Exclude<ConsentDecision, 'unset'>, decidedAt: string): void {
  void import('@/lib/legal/record')
    .then(({ recordAcceptance }) => recordAcceptance(decision))
    .then((saved) => {
      if (!saved) return;
      const stored = parseStored(readJson(CONSENT_STORAGE_KEY));
      if (stored?.decidedAt !== decidedAt) return; // the visitor chose again meanwhile: that choice saves itself
      writeJson(CONSENT_STORAGE_KEY, {
        v: CONSENT_VERSION,
        decision: stored.decision,
        decidedAt,
      } satisfies StoredConsent);
    })
    .catch(() => undefined);
}

function decide(
  decision: Exclude<ConsentDecision, 'unset'>,
): Pick<ConsentState, 'decision' | 'decidedAt'> {
  const decidedAt = new Date().toISOString();
  writeJson(CONSENT_STORAGE_KEY, {
    v: CONSENT_VERSION,
    decision,
    decidedAt,
    saved: false,
  } satisfies StoredConsent);
  saveToServer(decision, decidedAt);
  return { decision, decidedAt };
}

export const useConsentStore = create<ConsentState>()((set) => ({
  decision: 'unset',
  decidedAt: null,
  hydrated: false,

  accept: () => {
    set(decide('accepted'));
  },
  reject: () => {
    set(decide('rejected'));
  },
  reset: () => {
    removeStorage(CONSENT_STORAGE_KEY);
    set({ decision: 'unset', decidedAt: null });
  },
  hydrate: () => {
    const stored = parseStored(readJson(CONSENT_STORAGE_KEY));
    if (stored?.saved === false) saveToServer(stored.decision, stored.decidedAt); // the first try could not reach the server
    set({
      decision: stored?.decision ?? 'unset',
      decidedAt: stored?.decidedAt ?? null,
      hydrated: true,
    });
  },
}));

/** `true` only after an explicit Accept. Gate every analytics/marketing script on this. */
export const isAnalyticsAllowed = (): boolean => useConsentStore.getState().decision === 'accepted';

export interface ConsentApi {
  decision: ConsentDecision;
  /** Convenience flag: analytics may run. */
  analyticsAllowed: boolean;
  hydrated: boolean;
  accept: () => void;
  reject: () => void;
  reset: () => void;
}

export function useConsent(): ConsentApi {
  const decision = useConsentStore((state) => state.decision);
  const hydrated = useConsentStore((state) => state.hydrated);
  const accept = useConsentStore((state) => state.accept);
  const reject = useConsentStore((state) => state.reject);
  const reset = useConsentStore((state) => state.reset);
  return { decision, analyticsAllowed: decision === 'accepted', hydrated, accept, reject, reset };
}

/** Mount once (providers): reads the stored choice after the first client render. */
export function useConsentHydration(): void {
  useEffect(() => {
    useConsentStore.getState().hydrate();
  }, []);
}
